import React, { useState } from 'react';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  HelpCircle, Search, ChevronDown, ChevronRight,
  CalendarDays, Users, Ticket, CreditCard, Settings,
  MessageSquare, BarChart3, ShoppingCart, Tag, Clock,
  BookOpen, Zap, Shield, Globe, Bell, UserPlus
} from 'lucide-react';

const HelpSection = ({ icon: Icon, title, children, defaultOpen = false, id, isVisible = true, forceOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const effectiveOpen = forceOpen || isOpen;

  if (!isVisible) return null;

  return (
    <Card className="mb-4" id={id} data-testid={id ? `help-section-${id}` : undefined}>
      <CardHeader 
        className="cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Icon className="w-5 h-5 text-primary" />
            </div>
            <CardTitle className="text-lg">{title}</CardTitle>
          </div>
          {effectiveOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </div>
      </CardHeader>
      {effectiveOpen && (
        <CardContent className="pt-0">
          <div className="prose prose-sm max-w-none dark:prose-invert">
            {children}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

const HelpArticle = ({ title, children }) => (
  <div className="mb-6 last:mb-0">
    <h4 className="font-semibold text-base mb-2 text-foreground">{title}</h4>
    <div className="text-muted-foreground text-sm space-y-2">
      {children}
    </div>
  </div>
);

export const StudioHelpPage = () => {
  const [searchTerm, setSearchTerm] = useState('');

  // Bug R18 fix (Iter-66): each quickLink now has a section anchor id.
  // Clicking scrolls the matching HelpSection into view and expands it.
  const quickLinks = [
    { title: 'Create a Class', section: 'section-classes' },
    { title: 'Add Instructor', section: 'section-instructors' },
    { title: 'Set Up ClassPass', section: 'section-classpasses' },
    { title: 'Process Payment', section: 'section-pos' },
    { title: 'View Reports', section: 'section-reports' },
    { title: 'Embed Widget', section: 'section-widget' },
  ];

  // Bug R19 fix (Iter-66): pre-populated suggestions appear below the
  // search input to give users an obvious starting point instead of a
  // blank slate.
  const searchSuggestions = [
    'How do I create a recurring class?',
    'What is a ClassPass?',
    'How do I refund a booking?',
    'How do I connect RyftPay?',
    'How do I embed the widget?',
    'How do I add a voucher?',
  ];

  const handleQuickLink = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Give the layout a beat, then click the header to open the section.
      setTimeout(() => {
        const header = el.querySelector('[class*="cursor-pointer"]');
        if (header && !header.parentElement.querySelector('.prose')) header.click();
      }, 300);
    }
  };

  // Very lightweight full-page text filter. When the user types, we hide
  // any HelpSection whose title doesn't match. React children rendered
  // below all receive an isVisible prop derived from this term.
  const term = searchTerm.trim().toLowerCase();
  const matches = (title) => !term || title.toLowerCase().includes(term);

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 rounded-xl bg-primary/10">
              <HelpCircle className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Help Centre</h1>
              <p className="text-muted-foreground">Everything you need to manage your studio</p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input 
            placeholder="Search help articles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-12"
            data-testid="help-search-input"
          />
        </div>

        {/* Bug R19 fix: pre-populated suggestions. */}
        {!term && (
          <div className="mb-6" data-testid="help-search-suggestions">
            <p className="text-xs text-muted-foreground mb-2">Popular searches:</p>
            <div className="flex flex-wrap gap-2">
              {searchSuggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSearchTerm(s.replace(/\?$/, ''))}
                  className="text-xs px-3 py-1 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-colors"
                  data-testid={`help-suggestion-${s.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Quick Links */}
        <div className="mb-8">
          <h3 className="text-sm font-medium text-muted-foreground mb-3">Quick Links</h3>
          <div className="flex flex-wrap gap-2">
            {quickLinks.map((link) => (
              <Badge 
                key={link.section}
                variant="secondary" 
                className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors px-3 py-1"
                onClick={() => handleQuickLink(link.section)}
                data-testid={`help-quicklink-${link.section}`}
              >
                {link.title}
              </Badge>
            ))}
          </div>
        </div>

        {/* Help Sections */}
        <HelpSection icon={Zap} title="Getting Started" defaultOpen={true} id="section-getting-started" isVisible={matches("Getting Started")} forceOpen={!!term && matches("Getting Started")}>
          <HelpArticle title="Welcome to StudioScheduler">
            <p>StudioScheduler is your all-in-one platform for managing fitness and wellness studio operations. This guide will help you get started with the key features.</p>
          </HelpArticle>
          
          <HelpArticle title="Initial Setup Checklist">
            <ol className="list-decimal list-inside space-y-1">
              <li><strong>Complete your studio profile</strong> - Go to Settings and fill in your studio details, logo, and contact information.</li>
              <li><strong>Define activity types</strong> - Set up the types of classes you offer (yoga, pilates, HIIT, etc.) in Settings → Activity Types.</li>
              <li><strong>Add instructors</strong> - Create profiles for your instructors with their specialties and hourly rates.</li>
              <li><strong>Create ClassPasses</strong> - Set up the class packages you want to sell to clients.</li>
              <li><strong>Schedule your first class</strong> - Create classes with dates, times, instructors, and capacity.</li>
              <li><strong>Set up payments</strong> - Connect your RyftPay account in Settings → Payments to accept client payments for ClassPasses and bookings.</li>
            </ol>
          </HelpArticle>

          <HelpArticle title="Understanding the Dashboard">
            <p>Your dashboard shows key metrics at a glance:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Total Classes</strong> - Number of scheduled classes</li>
              <li><strong>Bookings</strong> - Total class bookings made</li>
              <li><strong>ClassPasses</strong> - Active ClassPasses sold</li>
              <li><strong>Revenue</strong> - Total revenue generated</li>
              <li><strong>Scheduled Classes</strong> - Upcoming classes with booking status</li>
            </ul>
          </HelpArticle>
        </HelpSection>

        <HelpSection icon={CalendarDays} title="Managing Classes" id="section-classes" isVisible={matches("Managing Classes")} forceOpen={!!term && matches("Managing Classes")}>
          <HelpArticle title="Creating a New Class">
            <ol className="list-decimal list-inside space-y-1">
              <li>Navigate to <strong>Classes</strong> from the sidebar</li>
              <li>Click the <strong>+ New Class</strong> button</li>
              <li>Fill in the class details:
                <ul className="list-disc list-inside ml-4 mt-1">
                  <li>Class name and description</li>
                  <li>Activity type (must be defined in Settings first)</li>
                  <li>Date, start time, and duration</li>
                  <li>Instructor assignment</li>
                  <li>Capacity and price per class</li>
                </ul>
              </li>
              <li>Click <strong>Create Class</strong> to save</li>
            </ol>
          </HelpArticle>

          <HelpArticle title="Creating Recurring Classes">
            <p>To create multiple classes at once:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Enable the <strong>Recurring</strong> toggle when creating a class</li>
              <li>Select which days of the week the class should repeat</li>
              <li>Choose the end date for the recurring series</li>
              <li>All classes will be created with the same details</li>
            </ol>
          </HelpArticle>

          <HelpArticle title="Cloning Classes">
            <p>To quickly duplicate an existing class:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Find the class you want to clone</li>
              <li>Click the <strong>Clone</strong> icon (copy symbol)</li>
              <li>Modify the date/time as needed</li>
              <li>Save the new class</li>
            </ol>
          </HelpArticle>

          <HelpArticle title="Editing and Cancelling Classes">
            <p><strong>To edit:</strong> Click the edit icon on any class to modify its details.</p>
            <p><strong>Recurring classes:</strong> If you edit a class that's part of a recurring series, you'll be asked whether the change should apply to <em>just this class</em> (e.g. a one-off instructor swap because your usual instructor is sick) or to <em>this and all future classes</em> in the series. Past classes are never modified.</p>
            <p><strong>Changing the instructor:</strong> Open Edit, pick the new instructor from the dropdown, and save. Existing bookings keep their seat — the instructor name on the class is updated automatically and shows on the schedule, in the client's dashboard, and on the studio's public booking widget.</p>
            <p><strong>To cancel:</strong> Click the delete/cancel icon. Note: Clients with bookings will be notified and credits may be refunded based on your cancellation policy.</p>
          </HelpArticle>
        </HelpSection>

        <HelpSection icon={Users} title="Managing Instructors" id="section-instructors" isVisible={matches("Managing Instructors")} forceOpen={!!term && matches("Managing Instructors")}>
          <HelpArticle title="Adding an Instructor">
            <ol className="list-decimal list-inside space-y-1">
              <li>Go to <strong>Instructors</strong> from the sidebar</li>
              <li>Click <strong>+ Add Instructor</strong></li>
              <li>Enter their details:
                <ul className="list-disc list-inside ml-4 mt-1">
                  <li>Name and email</li>
                  <li>Phone number</li>
                  <li>Bio/description</li>
                  <li>Specialties (activity types they teach)</li>
                  <li>Hourly rate for payroll tracking</li>
                </ul>
              </li>
              <li>Upload a profile photo (optional)</li>
              <li>Click <strong>Add Instructor</strong></li>
            </ol>
          </HelpArticle>

          <HelpArticle title="Instructor Specialties">
            <p>Specialties help you assign the right instructors to classes. When creating a class, you'll only see instructors who have the matching activity type as a specialty.</p>
          </HelpArticle>
        </HelpSection>

        <HelpSection icon={UserPlus} title="Managing Clients" id="section-clients" isVisible={matches("Managing Clients")} forceOpen={!!term && matches("Managing Clients")}>
          <HelpArticle title="Viewing Client Information">
            <p>The <strong>Clients</strong> page shows all clients who have booked at your studio, including:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Contact information</li>
              <li>ClassPass balance and history</li>
              <li>Booking history</li>
              <li>Saved payment methods</li>
            </ul>
          </HelpArticle>

          <HelpArticle title="Adjusting Client Credits">
            <p>To manually add or remove credits from a client's ClassPass:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Find the client in the Clients list</li>
              <li>Click on their ClassPass</li>
              <li>Use the <strong>Adjust Credits</strong> button</li>
              <li>Enter the number of credits to add (positive) or remove (negative)</li>
              <li>Add a note explaining the adjustment</li>
            </ol>
          </HelpArticle>

          <HelpArticle title="Cancelling Client Bookings">
            <p>If a client needs to cancel and can't do it themselves:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Go to the client's profile</li>
              <li>Find the booking in their history</li>
              <li>Click <strong>Cancel Booking</strong></li>
              <li>Credits will be refunded according to your cancellation policy</li>
            </ol>
          </HelpArticle>

          <HelpArticle title="Importing/Exporting Clients">
            <p><strong>Export:</strong> Click the export button to download a CSV of all your clients.</p>
            <p><strong>Import:</strong> Upload a CSV file with client data. Required columns: first_name, last_name, email. Optional: phone.</p>
          </HelpArticle>
        </HelpSection>

        <HelpSection icon={Ticket} title="ClassPasses & Memberships" id="section-classpasses" isVisible={matches("ClassPasses & Memberships")} forceOpen={!!term && matches("ClassPasses & Memberships")}>
          <HelpArticle title="Creating a ClassPass">
            <ol className="list-decimal list-inside space-y-1">
              <li>Go to <strong>ClassPasses</strong> from the sidebar</li>
              <li>Click <strong>+ New ClassPass</strong></li>
              <li>Set up the package:
                <ul className="list-disc list-inside ml-4 mt-1">
                  <li><strong>Name</strong> - e.g., "10-Class Yoga Pack"</li>
                  <li><strong>Activity Type</strong> - Which class types it can be used for</li>
                  <li><strong>Number of Classes</strong> - How many credits included</li>
                  <li><strong>Price</strong> - Total cost of the package</li>
                  <li><strong>Validity</strong> - Days until expiration</li>
                  <li><strong>Transferable</strong> - Can credits be shared?</li>
                </ul>
              </li>
            </ol>
          </HelpArticle>

          <HelpArticle title="Setting Up Memberships (Auto-Renewing ClassPasses)">
            <p>Memberships are ClassPasses that automatically renew:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>When creating a ClassPass, enable <strong>Studio Membership</strong></li>
              <li>Select the billing cycle (Monthly or Yearly)</li>
              <li>When clients purchase this, they must save a payment method</li>
              <li>When their credits run out or expire, they're automatically charged and credits are renewed</li>
            </ol>
            <p className="mt-2 text-amber-600">Note: Clients can disable auto-renewal from their account at any time.</p>
          </HelpArticle>
        </HelpSection>

        <HelpSection icon={Clock} title="Bookings & Waitlists" id="section-bookings" isVisible={matches("Bookings & Waitlists")} forceOpen={!!term && matches("Bookings & Waitlists")}>
          <HelpArticle title="How Bookings Work">
            <p>When a client books a class:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>One credit is deducted from their ClassPass</li>
              <li>They receive a confirmation (email/SMS based on your settings)</li>
              <li>They appear in the class roster</li>
              <li>Reminders are sent based on your reminder settings</li>
            </ol>
          </HelpArticle>

          <HelpArticle title="Two-Factor Authentication (MFA)">
            <p>For security, Studio Owners and Platform Admins must enable Two-Factor Authentication (MFA) using an authenticator app such as Google Authenticator, Microsoft Authenticator, or Authy.</p>
            <p><strong>Setting up MFA:</strong></p>
            <ul className="list-disc list-inside space-y-1">
              <li>Go to <em>My Profile</em> → <em>Two-Factor Authentication</em></li>
              <li>Click <strong>Enable MFA</strong></li>
              <li>Scan the QR code with your authenticator app, or type the secret in manually</li>
              <li>Enter the 6-digit code your app displays to confirm</li>
              <li>Save the 10 one-time backup codes somewhere safe (password manager, printed copy)</li>
            </ul>
            <p className="mt-3"><strong>Grace period:</strong> If MFA isn't set up yet, you have 5 logins to enroll. After that you'll be blocked from accessing the dashboard until you complete enrollment.</p>
            <p><strong>Lost your phone?</strong> Use one of your backup codes at the MFA prompt — click "Use a backup code instead". Each code works only once. If you've used all your codes, contact the platform admin to reset your MFA.</p>
            <p><strong>Platform admin reset:</strong> If a Studio Owner needs their MFA reset (lost device, no backup codes left), the Platform Admin can click the shield-off icon next to the studio on the Admin → Studios page. This clears the authenticator pairing and gives the owner 5 fresh logins to re-enroll.</p>
          </HelpArticle>

          <HelpArticle title="Managing Waitlists">
            <p>When a class is full:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Clients can join the waitlist for that class</li>
              <li>No credit is deducted for joining a waitlist spot</li>
              <li>The order of the waitlist is the order people joined</li>
            </ul>
            <p className="mt-3"><strong>When a seat opens (someone cancels):</strong></p>
            <ul className="list-disc list-inside space-y-1">
              <li>Every waitlisted client is notified by email/SMS at the same time</li>
              <li>The class enters a <strong>waitlist priority window</strong> (you configure the length on the <em>Settings → Cancellation Policy</em> tab — 0 minutes up to 4 hours, in 30-minute steps): only clients on the waitlist for that class can book the freed seat during this window</li>
              <li>It's first-come, first-served among the waitlisted clients — whoever books first gets the seat</li>
              <li>After the window expires (or immediately if you set 0) the class re-opens to everyone</li>
              <li>You can also manually promote someone from the waitlist using the bell icon on the class row</li>
            </ul>
            <p className="mt-3 text-xs text-muted-foreground">
              The priority window prevents a passer-by who happens to be on the booking page from grabbing the seat ahead of your waitlist. People who weren't on the waitlist will see "This class is in waitlist priority booking" if they try to book during the window. Set the duration to 0 if you'd rather notify waitlisters but let anyone book the freed seat immediately.
            </p>
          </HelpArticle>

          <HelpArticle title="Cancellation Policy">
            <p>Configure your cancellation policy in <strong>Settings</strong>:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Notice Period</strong> - Hours before class that free cancellation is allowed</li>
              <li><strong>Late Cancel Handling</strong> - Credit refund, no refund, or partial refund</li>
              <li><strong>Late Cancel Fee</strong> - Optional monetary penalty</li>
            </ul>
          </HelpArticle>
        </HelpSection>

        <HelpSection icon={Tag} title="Vouchers & Discounts" id="section-vouchers" isVisible={matches("Vouchers & Discounts")} forceOpen={!!term && matches("Vouchers & Discounts")}>
          <HelpArticle title="Creating a Voucher">
            <ol className="list-decimal list-inside space-y-1">
              <li>Go to <strong>Vouchers</strong> from the sidebar</li>
              <li>Click <strong>+ Create Voucher</strong></li>
              <li>Configure the voucher:
                <ul className="list-disc list-inside ml-4 mt-1">
                  <li><strong>Code</strong> - What clients enter (e.g., SUMMER20)</li>
                  <li><strong>Discount Type</strong> - Percentage or fixed amount</li>
                  <li><strong>Value</strong> - e.g., 20 for 20% or €20</li>
                  <li><strong>Usage Limit</strong> - How many times it can be used total</li>
                  <li><strong>Per-User Limit</strong> - How many times one person can use it</li>
                  <li><strong>Expiry Date</strong> - When the voucher stops working</li>
                </ul>
              </li>
            </ol>
          </HelpArticle>

          <HelpArticle title="Voucher Best Practices">
            <ul className="list-disc list-inside space-y-1">
              <li>Use clear, memorable codes (WELCOME10, SUMMER2024)</li>
              <li>Set expiry dates to create urgency</li>
              <li>Limit per-user usage to prevent abuse</li>
              <li>Track redemptions in the voucher list</li>
            </ul>
          </HelpArticle>
        </HelpSection>

        <HelpSection icon={ShoppingCart} title="Point of Sale (POS)" id="section-pos" isVisible={matches("Point of Sale (POS)")} forceOpen={!!term && matches("Point of Sale (POS)")}>
          <HelpArticle title="Using the POS System">
            <p>The POS allows you to charge clients directly using their saved cards:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Go to <strong>POS</strong> from the sidebar</li>
              <li>Select the client from the dropdown</li>
              <li>Choose which saved card to charge</li>
              <li>Select the ClassPass to purchase</li>
              <li>Optionally apply a voucher code</li>
              <li>Review the total and click <strong>Process Payment</strong></li>
            </ol>
          </HelpArticle>

          <HelpArticle title="When to Use POS">
            <ul className="list-disc list-inside space-y-1">
              <li>Walk-in purchases at reception</li>
              <li>Phone orders from existing clients</li>
              <li>Renewing a client's ClassPass on their behalf</li>
              <li>Applying special in-person discounts</li>
            </ul>
          </HelpArticle>
        </HelpSection>

        <HelpSection icon={CreditCard} title="Payments & Refunds" id="section-payments" isVisible={matches("Payments & Refunds")} forceOpen={!!term && matches("Payments & Refunds")}>
          <HelpArticle title="Setting Up Payments">
            <p>StudioScheduler uses RyftPay for payment processing:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Go to <strong>Subscription</strong> in the sidebar</li>
              <li>Complete the RyftPay onboarding process</li>
              <li>Verify your business details</li>
              <li>Once approved, you can accept card payments</li>
            </ol>
          </HelpArticle>

          <HelpArticle title="Processing Refunds">
            <p>To refund a payment:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Go to <strong>Reports</strong></li>
              <li>Find the transaction in the payment history</li>
              <li>Click <strong>Refund</strong></li>
              <li>Confirm the refund amount</li>
              <li>The refund will be processed to the original payment method</li>
            </ol>
            <p className="mt-2 text-amber-600">Note: Refunds may take 5-10 business days to appear on the client's statement.</p>
          </HelpArticle>

          <HelpArticle title="Saved Payment Methods">
            <p>Clients can save cards for:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Faster checkout</li>
              <li>Membership auto-renewals</li>
              <li>POS purchases</li>
            </ul>
            <p>Cards are securely stored by RyftPay - you never see full card numbers.</p>
          </HelpArticle>
        </HelpSection>

        <HelpSection icon={BarChart3} title="Reports & Analytics" id="section-reports" isVisible={matches("Reports & Analytics")} forceOpen={!!term && matches("Reports & Analytics")}>
          <HelpArticle title="Available Reports">
            <p>The <strong>Reports</strong> page provides insights into your business:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Revenue Overview</strong> - Total earnings, trends over time</li>
              <li><strong>Class Performance</strong> - Which classes are most popular</li>
              <li><strong>Instructor Stats</strong> - Classes taught, bookings per instructor</li>
              <li><strong>Client Retention</strong> - New vs returning clients</li>
              <li><strong>ClassPass Sales</strong> - Which packages sell best</li>
            </ul>
          </HelpArticle>

          <HelpArticle title="Exporting Data">
            <p>Most reports can be exported to CSV for further analysis in spreadsheet software.</p>
          </HelpArticle>
        </HelpSection>

        <HelpSection icon={MessageSquare} title="Communications" id="section-comms" isVisible={matches("Communications")} forceOpen={!!term && matches("Communications")}>
          <HelpArticle title="Automatic Notifications">
            <p>StudioScheduler sends automatic notifications for:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Booking confirmations</li>
              <li>Booking cancellations</li>
              <li>Waitlist promotions</li>
              <li>Class reminders (configurable timing)</li>
              <li>ClassPass purchase receipts</li>
              <li>Membership renewal notices</li>
            </ul>
          </HelpArticle>

          <HelpArticle title="Communication Channels">
            <p>Configure your preferred channels in <strong>Settings</strong>:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Email</strong> - Via SMTP2Go or Brevo</li>
              <li><strong>SMS</strong> - Via Brevo (requires plan upgrade)</li>
              <li><strong>WhatsApp</strong> - Via Brevo (requires plan upgrade and template approval)</li>
            </ul>
          </HelpArticle>

          <HelpArticle title="Class Reminders">
            <p>Set up automatic reminders in <strong>Settings → Reminders</strong>:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Enable class reminders</li>
              <li>Set hours before class (e.g., 24 hours, 2 hours)</li>
              <li>Choose reminder channels (email, SMS)</li>
              <li>Reminders run automatically - no manual action needed</li>
            </ol>
          </HelpArticle>

          <HelpArticle title="Viewing Communication Logs">
            <p>Go to <strong>Comms Log</strong> to see all sent notifications, their status, and any delivery errors.</p>
          </HelpArticle>
        </HelpSection>

        <HelpSection icon={Globe} title="Website Widget" id="section-widget" isVisible={matches("Website Widget")} forceOpen={!!term && matches("Website Widget")}>
          <HelpArticle title="Embedding Your Schedule">
            <p>Add your class schedule to your website:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Go to <strong>Settings → Widget</strong></li>
              <li>Copy the embed code provided</li>
              <li>Paste it into your website's HTML</li>
              <li>The widget shows your live schedule and allows bookings</li>
            </ol>
          </HelpArticle>

          <HelpArticle title="Widget Features">
            <ul className="list-disc list-inside space-y-1">
              <li>Shows upcoming classes with availability</li>
              <li>Clients can book directly (if logged in)</li>
              <li>Responsive design works on mobile</li>
              <li>Matches your brand colors (configurable)</li>
            </ul>
            <p className="mt-2 text-amber-600">Note: Widget embedding requires a plan that includes this feature.</p>
          </HelpArticle>
        </HelpSection>

        <HelpSection icon={Settings} title="Studio Settings" id="section-settings" isVisible={matches("Studio Settings")} forceOpen={!!term && matches("Studio Settings")}>
          <HelpArticle title="General Settings">
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Studio Name & Description</strong> - How your studio appears to clients</li>
              <li><strong>Logo & Cover Image</strong> - Branding images</li>
              <li><strong>Contact Information</strong> - Address, phone, email</li>
              <li><strong>Business Hours</strong> - When you're open</li>
            </ul>
          </HelpArticle>

          <HelpArticle title="Activity Types">
            <p>Activity types define the categories of classes you offer:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Go to <strong>Settings → Activity Types</strong></li>
              <li>Add types like "Yoga", "Pilates", "HIIT", "Strength"</li>
              <li>These are used when creating classes, ClassPasses, and instructor specialties</li>
            </ol>
            <p className="mt-2 text-amber-600">Important: You must define activity types before creating classes or ClassPasses.</p>
          </HelpArticle>

          <HelpArticle title="Cancellation Policy">
            <p>Configure how cancellations are handled:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Notice Hours</strong> - Free cancellation window</li>
              <li><strong>Refund Type</strong> - Credit back, no refund, or partial</li>
              <li><strong>Late Cancel Fee</strong> - Additional charge for late cancellations</li>
            </ul>
          </HelpArticle>
        </HelpSection>

        <HelpSection icon={Shield} title="Account & Security" id="section-security" isVisible={matches("Account & Security")} forceOpen={!!term && matches("Account & Security")}>
          <HelpArticle title="Changing Your Password">
            <p>To change your password, use the password reset option on the login page or contact support.</p>
          </HelpArticle>

          <HelpArticle title="Managing Staff Access">
            <p>Currently, each studio has one admin account. For additional staff access, contact support about multi-user accounts.</p>
          </HelpArticle>
        </HelpSection>

        <HelpSection icon={Bell} title="Troubleshooting" id="section-troubleshooting" isVisible={matches("Troubleshooting")} forceOpen={!!term && matches("Troubleshooting")}>
          <HelpArticle title="Clients Can't Book">
            <p>Check the following:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Does the client have a valid ClassPass with credits?</li>
              <li>Is the ClassPass activity type the same as the class?</li>
              <li>Has the ClassPass expired?</li>
              <li>Is the class full? (They may need to join waitlist)</li>
            </ul>
          </HelpArticle>

          <HelpArticle title="Payments Not Working">
            <ul className="list-disc list-inside space-y-1">
              <li>Verify your RyftPay account is fully onboarded</li>
              <li>Check that your subscription plan is active</li>
              <li>Ensure the client's card hasn't expired</li>
              <li>Contact support if issues persist</li>
            </ul>
          </HelpArticle>

          <HelpArticle title="Notifications Not Sending">
            <ul className="list-disc list-inside space-y-1">
              <li>Check your email provider settings (SMTP2Go/Brevo)</li>
              <li>Verify API keys are correct</li>
              <li>Check the Comms Log for error messages</li>
              <li>Ensure client email addresses are valid</li>
            </ul>
          </HelpArticle>
        </HelpSection>

        {/* Contact Support */}
        <Card className="mt-8 bg-primary/5 border-primary/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5" />
              Need More Help?
            </CardTitle>
            <CardDescription>
              Can't find what you're looking for? Our support team is here to help.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Contact us at <a href="mailto:support@studioscheduler.ie" className="text-primary font-medium hover:underline">support@studioscheduler.ie</a> or use the "Get Support" link in the sidebar.
            </p>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default StudioHelpPage;
